/*
 * Shared checklist editor: textarea (one item per line) + live numbered
 * preview + « Import from template » dropdown.
 *
 * Used by both the WorkSite form (legacy owner) and the Mission/Job
 * form (current owner since the checklist moved to the Job). The markup
 * contract is identical on both pages:
 *
 *   - ``#id_checklist_items``        the textarea (one item per line)
 *   - ``#preview-list`` / ``#preview-empty``   the live preview
 *   - ``#import-template-btn``       toggles the templates dropdown
 *   - ``#import-template-dropdown``  + ``.import-template-option[data-template-id]``
 *
 * The confirm-before-replace copy is read from the host ``<form>``
 * ``data-confirm-replace-*`` attributes.
 *
 * Binding follows hx-boost-init.mdc rule 3/4: per-element JS-property
 * flags (the textarea / button nodes are replaced on every swap, so a
 * module-level flag would wrongly short-circuit the 2nd visit).
 */

function escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
}

function initChecklistPreview(root) {
    const textarea = root.querySelector('#id_checklist_items');
    const previewList = root.querySelector('#preview-list');
    const previewEmpty = root.querySelector('#preview-empty');
    if (!textarea || !previewList || !previewEmpty) return;

    if (textarea._checklistPreviewBound) return;
    textarea._checklistPreviewBound = true;

    const updatePreview = () => {
        const text = (textarea.value || '').trim();
        const items = text.split('\n').filter((line) => line.trim() !== '');
        if (items.length === 0) {
            previewList.classList.add('hidden');
            previewEmpty.classList.remove('hidden');
            previewList.innerHTML = '';
            return;
        }
        previewList.classList.remove('hidden');
        previewEmpty.classList.add('hidden');
        previewList.innerHTML = items.map((item, index) => `
            <li class="flex items-center text-sm">
                <span class="flex-shrink-0 w-5 h-5 rounded-full bg-primary-100 dark:bg-primary-900 text-primary-600 dark:text-primary-400 flex items-center justify-center text-xs font-medium mr-2">
                    ${index + 1}
                </span>
                <span class="text-gray-700 dark:text-gray-300">${escapeHtml(item.trim())}</span>
            </li>
        `).join('');
    };

    textarea.addEventListener('input', updatePreview);
    setTimeout(updatePreview, 0);
}

function initChecklistTemplateImport(root) {
    const btn = root.querySelector('#import-template-btn');
    const dropdown = root.querySelector('#import-template-dropdown');
    const textarea = root.querySelector('#id_checklist_items');
    if (!btn || !dropdown || !textarea) return;
    if (btn._checklistImportBound) return;
    btn._checklistImportBound = true;

    const form = root.matches('form') ? root : root.closest('form');
    const opts = {
        title: (form && form.dataset.confirmReplaceTitle) || '',
        message: (form && form.dataset.confirmReplaceMessage) || '',
        confirmText: (form && form.dataset.confirmReplaceConfirm) || '',
        cancelText: (form && form.dataset.confirmReplaceCancel) || '',
    };

    const doImport = (templateId) => {
        fetch('/office/api/checklist-templates/' + templateId + '/')
            .then((r) => r.json())
            .then((data) => {
                if (!data || !Array.isArray(data.items)) return;
                const lines = data.items
                    .map((entry) => (typeof entry === 'string' ? entry : (entry.item || '')))
                    .filter((line) => line.trim() !== '');
                textarea.value = lines.join('\n');
                textarea.dispatchEvent(new Event('input'));
            });
    };

    const selectOption = (option) => {
        const templateId = option.dataset.templateId;
        dropdown.classList.add('hidden');
        const needsConfirm = (textarea.value || '').trim() && opts.message;
        if (needsConfirm && typeof window.showConfirmModal === 'function') {
            window.showConfirmModal({
                title: opts.title,
                message: opts.message,
                confirmText: opts.confirmText,
                cancelText: opts.cancelText,
                type: 'warning',
                onConfirm: () => { doImport(templateId); },
            });
            return;
        }
        if (needsConfirm && !window.confirm(opts.message)) return;
        doImport(templateId);
    };

    btn.addEventListener('click', (e) => {
        e.stopPropagation();
        dropdown.classList.toggle('hidden');
    });

    dropdown.querySelectorAll('.import-template-option').forEach((option) => {
        option.addEventListener('click', () => selectOption(option));
    });

    document.addEventListener('click', (e) => {
        if (!btn.contains(e.target) && !dropdown.contains(e.target)) {
            dropdown.classList.add('hidden');
        }
    });
}

export function initChecklistEditor(root) {
    if (!root) return;
    initChecklistPreview(root);
    initChecklistTemplateImport(root);
}
